import {GitMergeIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useRestoreHeadRefMutation} from '@github-ui/mergebox/use-restore-head-ref-mutation'
import {Button} from '@primer/react'
import type {ReactNode} from 'react'

import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {PullRequestState} from '../../../../types'
import {useInvalidatePrState} from '../../data/use-invalidate-pr-state'
import type {ViewerRole} from '../deciders/use-decide-next-action'
import {useMergeabilityData} from '../mergeability/use-mergeability-data'
import {FailureDialog} from '../../sidebar-actions/FailureDialog'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {DeleteHeadBranch, shouldShowDeleteHeadBranch} from './DeleteHeadBranch'
import {MergedDescription} from './MergedDescription'
import {RevertPRAction} from './RevertPRAction'

export function shouldShowMergedPR(pullRequestState: PullRequestState) {
  return pullRequestState === PullRequestState.Merged
}

export function MergedPR({variant, userRole}: {variant?: 'default' | 'embedded'; userRole?: ViewerRole}) {
  const pullRequestState = usePullRequestState()
  const mergeabilityData = useMergeabilityData()

  if (!shouldShowMergedPR(pullRequestState)) return null

  const isAuthor = !userRole || userRole === 'author'

  if (!isAuthor) {
    return <MergedStatus variant={variant} actionSlot={<RevertPRAction />} />
  }

  if (shouldShowDeleteHeadBranch(mergeabilityData)) {
    return <DeleteHeadBranch variant={variant} />
  }

  const canRestoreHeadRef = Boolean(mergeabilityData?.mergeBoxPullRequest.viewerCanRestoreHeadRef)

  return (
    <MergedStatus
      variant={variant}
      actionSlot={
        <span className="d-inline-flex flex-items-center gap-2">
          {canRestoreHeadRef ? <RestoreBranchAction /> : null}
          <RevertPRAction />
        </span>
      }
    />
  )
}

function MergedStatus({variant, actionSlot}: {variant?: 'default' | 'embedded'; actionSlot?: ReactNode}) {
  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={GitMergeIcon} variant="done" emphasized />}
      emphasizedLeadingVisual
      title="Merged"
      itemName="merged"
      variant={variant}
      description={<MergedDescription />}
      actionSlot={actionSlot}
    />
  )
}

function RestoreBranchAction() {
  const invalidatePrState = useInvalidatePrState()
  const restoreHeadRefMutation = useRestoreHeadRefMutation({
    onSuccess: () => {
      announce('Branch restored')
      invalidatePrState()
    },
  })

  return (
    <>
      <Button
        size="small"
        loading={restoreHeadRefMutation.isPending}
        loadingAnnouncement="Restoring branch"
        onClick={() => restoreHeadRefMutation.mutate()}
      >
        Restore branch
      </Button>
      {restoreHeadRefMutation.isError && (
        <FailureDialog
          title="Failed to restore branch"
          message="An error occurred while restoring the branch. Please try again."
          details={restoreHeadRefMutation.error.message}
          onRetry={() => restoreHeadRefMutation.mutate()}
          onDismiss={() => restoreHeadRefMutation.reset()}
        />
      )}
    </>
  )
}
